// GraphQL
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { publicClient } from 'application/middleware/ekardo/ekardoClient';
import cx from 'classnames';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import { PageContainer } from 'components/Container';
import LoadingSpinner from 'components/LoadingSpinner';
import gql from 'graphql-tag';
import isEqual from 'lodash.isequal';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import styles from './ContentWrapper.module.css';
import * as mutations from './mutations';
import * as queries from './queries';
import { GET_ANSWERS } from 'application/middleware/ekardo/queries/getAnswers';

// Components
const NoSSRContentWrapper = dynamic<{trackingId: number; pageId: number;}>(() => import('./NoSSRComp'), {
  ssr: false,
});


const CONTENT_PAGE_FRAGMENT = gql`
  fragment contentPageFieldsForMenu on ContentPageType {
    contentPageConfigurationNavigation {
      backButtonToolTip
      enableBackButton
      enableNextButton
      nextButtonToolTip
    }
    cssStyle {
      backgroundColor
      class
    }
    id
    isBookmarked
    isCompleted(stepTrackingId: $userFlowStepTrackId)
    sort
    title
    url
  }
`;

const GET_CONTENT_PAGES = gql`
  query getUserFlowStepContentPages($userFlowId: Int!, $userFlowStepTrackId: Int!, $userFlowStepUrl: String!) {
    userFlowStep {
      getUserFlowStepByName(userFlowId: $userFlowId, userFlowStepUrl: $userFlowStepUrl) {
        id
        contentGroup {
          id
          contentPages(status: [PUBLISHED, UNPUBLISHED], topLevelOnly: true) {
            ...contentPageFieldsForMenu
            contentPageChildren(status: [PUBLISHED, UNPUBLISHED]) {
              ...contentPageFieldsForMenu
              contentPageChildren(status: [PUBLISHED, UNPUBLISHED]) {
                ...contentPageFieldsForMenu
                contentPageChildren(status: [PUBLISHED, UNPUBLISHED]) {
                  ...contentPageFieldsForMenu
                }
              }
            }
          }
          iconAsset {
            contentType
            s3Key
            presignedUrl
          }
          title
          url
        }
      }
    }
  }
  ${CONTENT_PAGE_FRAGMENT}
`;

const GET_USER_FLOW_STEP = gql`
  query getUserFlowStep($userFlowId: Int!, $userFlowStepUrl: String!) {
    userFlowStep {
      getUserFlowStepByName(userFlowId: $userFlowId, userFlowStepUrl: $userFlowStepUrl) {
        contentGroup {
          id
        }
        id
        isComplete
        repetitionAllowed
        userRepetition {
          completed
          id
        }
      }
    }
  }
`;

interface ContentGroupType {
  iconAsset: IconAssetType;
  title: string;
  url: string;
}

interface ContentPageConfigurationNavigationType {
  backButtonToolTip: string;
  enableBackButton: boolean;
  enableNextButton: boolean;
  nextButtonToolTip: string;
}

interface ContentPageProps {
  setGroupName?: (name: string) => void;
  setPageName?: (name: string) => void;
}

interface IconAssetType {
  contentType?: string;
  s3Key?: string;
  presignedUrl: string;
}
interface NextButtonProps {
  contentPage: ContentPageType;
  enable: boolean;
  flattenedContentPages: ContentPageType[];
  handleClick: () => void;
  isToolBox: boolean;
  tooltip: string;
}

interface CssStyle {
  backgroundColor?: string;
  class?: string;
}

export interface ContentPageType {
  contentPageChildren?: ContentPageType[];
  contentPageConfigurationNavigation?: ContentPageConfigurationNavigationType;
  id: number;
  isBookmarked?: boolean;
  cssStyle?: CssStyle;
  isCompleted?: boolean;
  sort?: number;
  title?: string;
  treeMenuKey?: string;
  url: string;
}

function QuizContentWrapper({ setGroupName, setPageName }: ContentPageProps) {
  const MODULE_3_INTRODUCTION_CONTENT_PAGE_ID = 105692;
  const MODULE_3_INTRODUCTION_QUESTION_ID = 14144;

  const router = useRouter();

  const { pageUrl } = router.query;

  const stepUrl = 'eligibility-quiz'

  const userFlowStepUrl = String(stepUrl);

  const contentPagePredicate = ({ url }: ContentPageType) => url.toLowerCase() === String(pageUrl).toLowerCase();
  const isToolBox = String(userFlowStepUrl).toLowerCase() === 'financial-effects';

  let userFlowId = 118;

  // eslint-disable-next-line no-undef
  if (stepUrl.indexOf('mini-module-') !== -1) {
    userFlowId = Number(process.env.REACT_APP_MINIMODULE_USER_FLOW);
  } else if (stepUrl.indexOf('financial-effects') !== -1) {
    userFlowId = Number(process.env.REACT_APP_TOOLBOX_USER_FLOW);
  }

  const TRACK_CACHE_KEY = `quiz_track_${userFlowId}`;

  const client = useRef(publicClient).current;

  const contentGroupRef = useRef<ContentGroupType>(null);
  const contentPageRef = useRef<ContentPageType>(null);

  const contentPagesRef = useRef<ContentPageType[]>(null);
  const flattenedContentPagesRef = useRef<ContentPageType[]>(null);

  const contentPageCompletionIdRef = useRef<number>();
  const userFlowStepTrackIdRef = useRef<number>();
  const pageUrlRef = useRef(pageUrl);
  const isNavigatingRef = useRef(false);

  // Two-slot double-buffer: keeps the next page pre-loaded so navigation is instant
  const visibleSlotRef = useRef(0);
  const slotsRef = useRef<{ pageId: number | null }[]>([{ pageId: null }, { pageId: null }]);

  const [contentPageConfigurationNavigation, setContentPageConfigurationNavigation] = useState(null);
  const [contentPageMenuItemsOpen, setContentPageMenuItemsOpen] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(true);
  const [userFlowStepTracks, setUserFlowStepTracks] = useState(undefined);
  const [visibleSlot, setVisibleSlot] = useState(0);
  const [slot0PageId, setSlot0PageId] = useState<number | null>(null);
  const [slot1PageId, setSlot1PageId] = useState<number | null>(null);

  const [callGetContentPagesQuery, { error: loadingContentPageError, loading: isGetContentPagesLoading }] = useLazyQuery(
    GET_CONTENT_PAGES,
    {
      client,
      fetchPolicy: 'cache-first',
      onCompleted(response) {
        const {
          userFlowStep: {
            getUserFlowStepByName: { contentGroup },
          },
        } = response;

        handleGetContentPageData(contentGroup);
      },
    },
  );

  const [callGetUserFlowStepQuery, { error: loadingUserFlowStepsError, loading: isGetUserFlowStepLoading }] = useLazyQuery(
    GET_USER_FLOW_STEP,
    {
      client,
      fetchPolicy: 'cache-and-network',
      onCompleted(response) {
        const {
          userFlowStep: { getUserFlowStepByName: userFlowStep },
        } = response;

        handleGetUserFlowStep(userFlowStep);
      },
    },
  );

  const [callGetNextPage] = useLazyQuery(queries.GET_NEXT_PAGE, {
    client,
    fetchPolicy: 'no-cache',
    onCompleted(response) {
      const {
        contentPage: { getNextOrPreviousPage: contentPage },
      } = response;

      if (contentPageId === MODULE_3_INTRODUCTION_CONTENT_PAGE_ID) {
        executeGetAnswers({
          variables: {
            questionIds: [MODULE_3_INTRODUCTION_QUESTION_ID],
          },
        });
        return;
      }

      handleGetNextPage(
        contentPage,
        contentPageCompletionIdRef.current,
        contentPagePredicate,
        contentPagesRef.current,
        isToolBox,
        userFlowStepTrackIdRef.current,
        userFlowStepUrl,
      );
    },
  });

  const [callEndModule] = useMutation(mutations.END_MODULE_MUTATION, {
    client,
    errorPolicy: 'ignore',
    onCompleted() {
      handleExit();
    },
    onError() {
      handleExit();
    },
  });

  const [callEndPage] = useMutation(mutations.END_PAGE_MUTATION, {
    client,
    errorPolicy: 'ignore',
  });

  const [callStartModule, { loading: isStartModuleLoading }] = useMutation(mutations.START_MODULE_MUTATION, {
    client,
    onCompleted(response) {
      const {
        userFlowStepTrack: { startUserFlowStep: userFlowStepTrack },
      } = response;

      handleStartModule(userFlowStepTrack, userFlowStepUrl);
    },
  });

  const [callStartPage] = useMutation(mutations.START_PAGE_MUTATION, {
    client,
    onCompleted(response) {
      const {
        userFlowStepTrack: { startContentPageCompletion: contentPageCompletion },
      } = response;

      handleStartPage(contentPageCompletion);
    },
  });

  function findContentPageRecursive(
    contentPages: ContentPageType[],
    predicate: (value: ContentPageType, index: number, obj: ContentPageType[]) => unknown,
  ) {
    if (!contentPages) {
      return;
    }

    if (!contentPages.length) {
      return;
    }

    const contentPageIndexFound = contentPages.findIndex(predicate);
    if (contentPageIndexFound !== -1) {
      return contentPages[contentPageIndexFound];
    }

    for (const contentPage of contentPages) {
      const childContentPage = findContentPageRecursive(contentPage.contentPageChildren, predicate);
      if (childContentPage) {
        return childContentPage;
      }
    }
  }

  function getFlattenedContentPages(contentPages: ContentPageType[], treeMenuKeys = '') {
    return contentPages.reduce((acc, contentPage) => {
      const { contentPageChildren } = contentPage;

      const treeMenuKey = treeMenuKeys.length ? `${treeMenuKeys},${treeMenuKeys}/${contentPage.id}` : contentPage.id.toString();
      const updatedContentPage = { ...contentPage, treeMenuKey };

      acc.push(updatedContentPage);

      if (contentPageChildren) {
        getFlattenedContentPages(contentPageChildren, treeMenuKey).forEach((contentPage) => acc.push(contentPage));
      }

      return acc;
    }, []);
  }

  function getNextButtonText(contentPages: ContentPageType[], currentContentPage: ContentPageType, isToolBox: boolean) {
    const currentContentPageIndex = contentPages.findIndex(({ id }) => currentContentPage.id === id);
    const totalContentPages = contentPages.length - 1;
    const percentageCompleted = parseFloat((currentContentPageIndex / totalContentPages).toFixed(2));

    const isLastPage = percentageCompleted === 1 || contentPages.length <= 1;

    if (isLastPage && isToolBox) {
      return 'Back to your toolbox';
    } else if (isLastPage) {
      return 'Sign up';
    } else {
      return 'Next';
    }
  }

  function goToContentPage(contentPageUrl: string, isToolBox: boolean, userFlowStepUrl: string) {
    router.push(`/[stepUrl]/[pageUrl]`, `/${userFlowStepUrl}/${contentPageUrl}`, { shallow: true });
  }

  function handleClickNext(contentPageId: number, userFlowStepTrackId: number) {
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;

    // MODULE_3 has server-side answer branching — must use the network call
    if (contentPageId === MODULE_3_INTRODUCTION_CONTENT_PAGE_ID) {
      callGetNextPage({
        variables: {
          contentPageId,
          navigation: 'NEXT',
          userFlowStepTrackId,
        },
      });
      return;
    }

    const flattenedPages = flattenedContentPagesRef.current;
    const currentIndex = flattenedPages.findIndex(({ id }) => id === contentPageId);
    const nextPage = flattenedPages[currentIndex + 1] ?? null;

    markPageComplete(flattenedPages, contentPagePredicate);
    callEndPage({ variables: { contentPageCompletionId: contentPageCompletionIdRef.current } });

    if (!nextPage) {
      callEndModule({ variables: { userFlowStepTrackId } });
      return;
    }

    callStartPage({ variables: { contentPageId: nextPage.id, userFlowStepTrackId } });
    goToContentPage(nextPage.url, isToolBox, userFlowStepUrl);
  }

  function handleExit() {
    sessionStorage.removeItem(TRACK_CACHE_KEY);
    router.push('/register');
  }

  function handleGetContentPageData(contentGroup) {
    const { contentPages } = contentGroup;

    if (!contentGroupRef.current || !isEqual(contentGroupRef.current, contentGroup)) {
      contentGroupRef.current = contentGroup;
      setGroupName(contentGroupRef.current.title);
    }

    if (!contentPagesRef.current || !isEqual(contentPagesRef.current, contentPages)) {
      contentPagesRef.current = contentPages;
      flattenedContentPagesRef.current = getFlattenedContentPages(contentPages);
    }

    contentPageRef.current = findContentPageRecursive(flattenedContentPagesRef.current, ({ url }) => url === pageUrlRef.current);
    setPageName(contentPageRef.current.title);

    if (
      (contentPageRef.current && !contentPageConfigurationNavigation) ||
      contentPageRef.current.contentPageConfigurationNavigation !== contentPageConfigurationNavigation
    ) {
      setContentPageConfigurationNavigation(contentPageRef.current.contentPageConfigurationNavigation);
    }

    const treeMenuKeys = new Set(contentPageRef.current.treeMenuKey.split(','));
    const openTreeMenuKeys = Array.from(treeMenuKeys);

    if (!isEqual(contentPageMenuItemsOpen, openTreeMenuKeys)) {
      setContentPageMenuItemsOpen(openTreeMenuKeys);
    }

    if (userFlowStepTrackIdRef.current && !contentPageCompletionIdRef.current) {
      callStartPage({ variables: { contentPageId: contentPageRef.current.id, userFlowStepTrackId: userFlowStepTrackIdRef.current } });
    }

    const currentPageId = contentPageRef.current.id;
    const hiddenSlot = visibleSlotRef.current === 0 ? 1 : 0;
    const hiddenPageId = slotsRef.current[hiddenSlot].pageId;

    // If the hidden slot already has this page pre-loaded, swap to it (no flash)
    const newVisibleSlot = hiddenPageId === currentPageId ? hiddenSlot : visibleSlotRef.current;
    const newHiddenSlot = newVisibleSlot === 0 ? 1 : 0;

    // Prefetch next page into the hidden slot
    const flatPages = flattenedContentPagesRef.current;
    const curIdx = flatPages.findIndex(({ id }) => id === currentPageId);
    const nextPageId = flatPages[curIdx + 1]?.id ?? null;

    slotsRef.current[newVisibleSlot] = { pageId: currentPageId };
    slotsRef.current[newHiddenSlot] = { pageId: nextPageId };
    visibleSlotRef.current = newVisibleSlot;
    isNavigatingRef.current = false;

    setVisibleSlot(newVisibleSlot);
    if (newVisibleSlot === 0) {
      setSlot0PageId(currentPageId);
      setSlot1PageId(nextPageId);
    } else {
      setSlot1PageId(currentPageId);
      setSlot0PageId(nextPageId);
    }
  }

  function handleGetNextPage(
    contentPage: ContentPageType,
    contentPageCompletionId: number,
    contentPagePredicate: ({ url }: ContentPageType) => boolean,
    contentPages: ContentPageType[],
    isToolBox: boolean,
    userFlowStepTrackId: number,
    userFlowStepUrl: string,
  ) {
    if (contentPage === null) {
      callEndPage({
        variables: {
          contentPageCompletionId,
        },
      });

      callEndModule({
        variables: {
          userFlowStepTrackId,
        },
      });

      return;
    }

    const { id: contentPageId, url: contentPageUrl } = contentPage;

    markPageComplete(contentPages, contentPagePredicate);

    callEndPage({
      variables: {
        contentPageCompletionId,
      },
    });

    callStartPage({
      variables: {
        contentPageId,
        userFlowStepTrackId,
      },
    });

    goToContentPage(contentPageUrl, isToolBox, userFlowStepUrl);
  }

  function handleGetUserFlowStep(userFlowStep: { id: number; userRepetition: any }) {
    const { id: userFlowStepId, userRepetition: userFlowStepTracks } = userFlowStep;

    setUserFlowStepTracks(userFlowStepTracks);

    callStartModule({
      variables: {
        userFlowStepId,
      },
    });
  }

  function handleStartModule(userFlowStepTrack, userFlowStepUrl: string) {
    const incompleteUserFlowStepTrack = userFlowStepTracks.find(({ completed }) => !completed);
    const userFlowStepTrackId = incompleteUserFlowStepTrack ? incompleteUserFlowStepTrack.id : userFlowStepTrack.id;

    userFlowStepTrackIdRef.current = userFlowStepTrackId;
    sessionStorage.setItem(TRACK_CACHE_KEY, userFlowStepTrackId.toString());

    callGetContentPagesQuery({
      variables: {
        userFlowId,
        userFlowStepTrackId: userFlowStepTrackIdRef.current,
        userFlowStepUrl,
      },
    });
  }

  function handleStartPage(contentPageCompletion) {
    const { id } = contentPageCompletion;

    contentPageCompletionIdRef.current = id;
  }

  function markPageComplete(contentPages: ContentPageType[], contentPagePredicate) {
    const currentContentPage = findContentPageRecursive(contentPages, contentPagePredicate);
    currentContentPage.isCompleted = true;
  }

  const [executeGetAnswers] = useLazyQuery(GET_ANSWERS, {
    client,
    fetchPolicy: 'cache-and-network',
    onCompleted(response) {
      const {
        questionAnswer: { getAnswersByQuestionIds: answers },
      } = response;

      const answer = answers.find(({ contentSnippetQuestionId }) => contentSnippetQuestionId === 14144)?.questionResponse?.id;

      const answerMap = {
        1167: 2420,
        1168: 105701,
        1169: 105713,
      };

      const newContentPageId = answerMap[answer];
      const newContentPage = contentPagesRef.current.find(({ id }) => id === newContentPageId);

      handleGetNextPage(
        newContentPage,
        contentPageCompletionIdRef.current,
        contentPagePredicate,
        contentPagesRef.current,
        isToolBox,
        userFlowStepTrackIdRef.current,
        userFlowStepUrl,
      );
    },
  });

  useEffect(() => {
    if (isMenuOpen == null) {
      // eslint-disable-next-line no-undef
      setIsMenuOpen(window.matchMedia('(min-width: 576px)').matches);
    }
  }, []);

  useEffect(() => {
    const cachedTrackId = Number(sessionStorage.getItem(TRACK_CACHE_KEY));
    if (cachedTrackId) {
      userFlowStepTrackIdRef.current = cachedTrackId;
      callGetContentPagesQuery({
        variables: {
          userFlowId,
          userFlowStepTrackId: cachedTrackId,
          userFlowStepUrl,
        },
      });
    } else {
      callGetUserFlowStepQuery({
        variables: {
          userFlowId,
          userFlowStepUrl,
        },
      });
    }
  }, [userFlowStepUrl]);

  useEffect(() => {
    pageUrlRef.current = pageUrl;
  }, [pageUrl]);

  useEffect(() => {
    if (!loadingContentPageError) return;
    // If a cached track ID caused this failure, clear it and retry via the normal flow
    if (!sessionStorage.getItem(TRACK_CACHE_KEY)) return;
    sessionStorage.removeItem(TRACK_CACHE_KEY);
    userFlowStepTrackIdRef.current = undefined;
    callGetUserFlowStepQuery({ variables: { userFlowId, userFlowStepUrl } });
  }, [loadingContentPageError]);

  useEffect(() => {
    if (!flattenedContentPagesRef.current || !userFlowStepTrackIdRef.current) {
      return;
    }
    callGetContentPagesQuery({
      variables: {
        userFlowId,
        userFlowStepTrackId: userFlowStepTrackIdRef.current,
        userFlowStepUrl,
      },
    });
  }, [pageUrl]);

  const isLoading =
    !contentPageRef.current ||
    !contentPagesRef.current ||
    !userFlowStepTrackIdRef.current ||
    isGetContentPagesLoading ||
    isGetUserFlowStepLoading ||
    isStartModuleLoading;

  const loadingError = loadingContentPageError || loadingUserFlowStepsError;

  if (loadingError) {
    return <div>{loadingError.message}</div>;
  }

  if (isMenuOpen == null) {
    return null;
  }

  const { enableNextButton = false, nextButtonToolTip = '' } =
    contentPageConfigurationNavigation || {};

  const { title: contentGroupTitle, url: contentGroupUrl } = contentGroupRef.current || {};
  const { id: contentPageId, title: contentPageTitle, url: contentPageUrl } = contentPageRef.current || {};

  function NextButton(props: NextButtonProps) {
    const { contentPage, enable, flattenedContentPages, handleClick, isToolBox, tooltip } = props;

    if (!enable) {
      return null;
    }

    const nextButtonText = getNextButtonText(flattenedContentPages, contentPage, isToolBox);

    return (
      <Button color="primary-inverse" title={enable && tooltip} onClick={handleClick}>
        {nextButtonText} <img className="m-l--small" src="/images/icon-arrow.svg" />
      </Button>
    );
  }

  const { cssStyle } = contentPageRef.current || {};
  const { backgroundColor, class: className } = cssStyle || {};
  const style = backgroundColor ? { backgroundColor } : {};

  return (
    <>
      {isLoading && slot0PageId === null && slot1PageId === null && <LoadingSpinner />}
      {(slot0PageId !== null || slot1PageId !== null) && (
        <div className={cx(styles['wrapper'], className && `${className}-page`, 'print-bg-white')} style={style}>
          <div className={cx(styles['content'])}>
            <PageContainer
              cmsContent
              breadcrumbs={[
                {
                  href: '/',
                  title: 'Home',
                },
                {
                  as: `/${contentGroupUrl}/${contentPageUrl}`,
                  href: '/[stepUrl]/[pageUrl]',
                  title: contentGroupTitle,
                },
                {
                  as: `/${contentGroupUrl}/${contentPageUrl}`,
                  href: '/module/[stepUrl]/[pageUrl]',
                  title: contentPageTitle,
                },
              ]}
              pageTitle={contentPageTitle}
              title={contentGroupTitle}
            >
              {slot0PageId !== null && (
                <div key="slot-0" style={visibleSlot !== 0 ? { display: 'none' } : {}}>
                  <NoSSRContentWrapper trackingId={Number(userFlowStepTrackIdRef.current)} pageId={slot0PageId} />
                </div>
              )}
              {slot1PageId !== null && (
                <div key="slot-1" style={visibleSlot !== 1 ? { display: 'none' } : {}}>
                  <NoSSRContentWrapper trackingId={Number(userFlowStepTrackIdRef.current)} pageId={slot1PageId} />
                </div>
              )}
            </PageContainer>

            <div className={styles['bottom']}>
              <ButtonGroup className={cx(styles['page-navigation'], 'no-print')}>
                <div className="justify-end l-flex l-flex-one">
                  <NextButton
                    contentPage={contentPageRef.current}
                    enable={enableNextButton}
                    flattenedContentPages={flattenedContentPagesRef.current}
                    handleClick={() => handleClickNext(contentPageId, userFlowStepTrackIdRef.current)}
                    isToolBox={isToolBox}
                    tooltip={nextButtonToolTip}
                  />
                </div>
              </ButtonGroup>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QuizContentWrapper;
